import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CNPJValidation, CPFValidation } from '@shared/constants';
import { ModelForm, SelectData } from '@shared/models';
import { FormInput } from '@shared/models/form-input.model';
import { BankFormData } from '@shared/models/bank.model';
import { formatCurrentAccount } from '@shared/functions/current-acount-mask';
import { PixValidators } from '@shared/functions/pix-validators';
import { removeMask } from '@shared/functions/removeMask';

export type PixKeyType = 'email' | 'cpf' | 'cnpj' | 'randomKey' | 'phone';

export interface BankAccountFormOptions {
  pixRequired?: boolean;
}

const defaultBankAccountOptions: Required<BankAccountFormOptions> = {
  pixRequired: false,
};

export function createBankAccountForm(
  options: BankAccountFormOptions = {},
): FormGroup<ModelForm<BankFormData>> {
  const resolvedOptions = resolveBankAccountOptions(options);

  return new FormGroup<ModelForm<BankFormData>>({
    banco: new FormControl({ label: 'ID CORRETORA DE TÍTULOS E VALORES MOBILIÁRIOS S.A. ', value: 254 } as SelectData, {
      validators: [Validators.required],
      nonNullable: true,
    }),
    agencia: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    conta: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    codBancario: new FormControl('', { nonNullable: true }),
    tipoConta: new FormControl({ label: '', value: '' } as SelectData, {
      validators: [Validators.required],
      nonNullable: true,
    }),
    chavePix: new FormControl('', {
      validators: resolvedOptions.pixRequired ? [Validators.required] : [],
      nonNullable: true,
    }),
  });
}

export function resetBankAccountForm(
  formGroup: FormGroup<ModelForm<BankFormData>>,
): void {
  formGroup.reset({
    banco: { label: '', value: '' } as SelectData,
    agencia: '',
    conta: '',
    codBancario: '',
    tipoConta: { label: '', value: '' } as SelectData,
    chavePix: '',
  });
}

export function setupBankAccountFormBehavior(
  formGroup: FormGroup<ModelForm<BankFormData>>,
  inputs: FormInput[],
  options: BankAccountFormOptions = {},
): Subscription {
  const resolvedOptions = resolveBankAccountOptions(options);
  const subscription = new Subscription();

  subscription.add(
    formGroup.controls.conta.valueChanges.subscribe((value) => {
      formGroup.controls.conta.setValue(formatCurrentAccount(value), {
        emitEvent: false,
      });
    }),
  );

  subscription.add(
    formGroup.controls.chavePix.valueChanges.subscribe((value) => {
      applyPixKeyValidation(formGroup, inputs, value ?? '', resolvedOptions);
    }),
  );

  applyPixKeyValidation(
    formGroup,
    inputs,
    formGroup.controls.chavePix.value ?? '',
    resolvedOptions,
  );

  return subscription;
}

export function detectPixKeyType(value: string | null | undefined): PixKeyType | null {
  if (!value) {
    return null;
  }

  const trimmedValue = String(value).trim();
  const cleanValue = removeMask(trimmedValue);

  if (trimmedValue.includes('@')) {
    return 'email';
  }

  if (/[a-fA-F]/.test(trimmedValue) && trimmedValue.includes('-')) {
    return 'randomKey';
  }

  if (cleanValue.length <= 11) {
    if (cleanValue.length === 11 && CPFValidation(cleanValue)) {
      return 'cpf';
    }

    if (cleanValue.length >= 10) {
      return 'phone';
    }

    return null;
  }

  if (cleanValue.length === 14 && CNPJValidation(cleanValue)) {
    return 'cnpj';
  }

  return null;
}

export function resolvePixKeyTypeId(type: PixKeyType | null): number {
  const typeMap: Record<PixKeyType, number> = {
    email: 1,
    cpf: 2,
    cnpj: 2,
    randomKey: 3,
    phone: 4,
  };

  return type ? typeMap[type] : 0;
}

function applyPixKeyValidation(
  formGroup: FormGroup<ModelForm<BankFormData>>,
  inputs: FormInput[],
  value: string,
  options: Required<BankAccountFormOptions>,
): void {
  const pixControl = formGroup.controls.chavePix;
  const pixInput = inputs.find((input) => input.formControlName === 'chavePix');
  const trimmedValue = String(value ?? '').trim();
  const cleanValue = removeMask(trimmedValue);
  const hasLetters = /[a-zA-Z]/.test(trimmedValue);
  const validators = options.pixRequired ? [Validators.required] : [];

  if (pixInput) {
    pixInput.mask = '';
  }

  if (!trimmedValue) {
    pixControl.setValidators(validators);
    pixControl.setErrors(null);
    pixControl.updateValueAndValidity({ emitEvent: false });
    return;
  }

  const pixKeyType = detectPixKeyType(trimmedValue);

  if (trimmedValue.includes('@')) {
    validators.push(PixValidators.email);
  } else if (pixKeyType === 'randomKey') {
    validators.push(PixValidators.randomKey);
  } else if (hasLetters) {
    validators.push(PixValidators.email);
  } else if (cleanValue.length <= 11) {
    if (pixInput) {
      pixInput.mask = '(00) 00000-0000999';
    }

    if (pixKeyType === 'cpf') {
      if (pixInput) {
        pixInput.mask = '000.000.000-00';
      }
      validators.push(PixValidators.cpf);
    } else {
      validators.push(PixValidators.phone);
    }
  } else {
    if (pixInput) {
      pixInput.mask = '00.000.000/0000-00';
    }

    if (pixKeyType === 'cnpj') {
      validators.push(PixValidators.cnpj);
    }
  }

  pixControl.setValidators(validators);
  pixControl.updateValueAndValidity({ emitEvent: false });
}

function resolveBankAccountOptions(
  options: BankAccountFormOptions,
): Required<BankAccountFormOptions> {
  return {
    ...defaultBankAccountOptions,
    ...options,
  };
}